import { Docx } from '@tiptap-pro/extension-export-docx';

import { colors } from 'theme/colors';

const { LineRuleType, Paragraph } = Docx;

interface BlockMetrics {
  fontPx: number;
  linePx: number;
  beforePx?: number;
  afterPx?: number;
  bold?: boolean;
}

// Computed styles of `.tiptap.ProseMirror`, measured in Chrome — the `em` cascades and
// the UA heading margins don't read reliably off the SCSS. No font family: index.css
// asks for Roboto but never loads it, so the editor falls back to the system sans,
// which is metric-compatible with the exporter's Arial.
const PARAGRAPH: BlockMetrics = { fontPx: 15, linePx: 24 };

// paragraph-node.scss wins over heading-node.scss — equal specificity, imported later.
const HEADING_1: BlockMetrics = { fontPx: 37.5, linePx: 56.25, beforePx: 75, afterPx: 25.125, bold: true };
const HEADING_2: BlockMetrics = { fontPx: 33.75, linePx: 50.625, beforePx: 50.625, afterPx: 28.0125, bold: true };
const HEADING_3: BlockMetrics = { fontPx: 31.875, linePx: 47.8125, beforePx: 31.875, afterPx: 31.875, bold: true };
const HEADING_4: BlockMetrics = { fontPx: 30, linePx: 45, beforePx: 30, afterPx: 39.9, bold: true };
const HEADING_5: BlockMetrics = { fontPx: 12.45, linePx: 18.675, beforePx: 20.7915, afterPx: 20.7915, bold: true };
const HEADING_6: BlockMetrics = { fontPx: 10.05, linePx: 15.075, beforePx: 23.4165, afterPx: 23.4165, bold: true };

export const toDocxHex = (value: unknown): string | undefined =>
  typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value) ? value.slice(1) : undefined;

export const HEADING_TEXT_COLOR = toDocxHex(colors.grey[900]);

const EXPORTER_HEADING_DEFAULTS_OVERRIDE = { color: HEADING_TEXT_COLOR, italics: false };

const LIST_ITEM: BlockMetrics = { fontPx: 15, linePx: 22.5 };

const QUOTE: BlockMetrics = { fontPx: 15, linePx: 22.5, beforePx: 15, afterPx: 15 };

const TABLE_CELL_PADDING_PX = 5;

const pxToTwips = (px: number): number => Math.round(px * 15);

// Word snaps `w:sz` to whole half-points and ignores `w:w` scaling, so the leftover
// fraction of a size like 11.25pt is carried as character spacing instead. Arial's mean
// advance is about 0.5em, making a 1pt shortfall worth 10 twips per character.
function runSize(px: number): { size: number; sizeComplexScript: number; characterSpacing?: number } {
  const size = Math.round(px * 1.5);
  const characterSpacing = Math.round((px * 0.75 - size / 2) * 10);
  return { size, sizeComplexScript: size, ...(characterSpacing === 0 ? {} : { characterSpacing }) };
}

function blockStyle({ fontPx, linePx, beforePx = 0, afterPx = 0, bold }: BlockMetrics) {
  return {
    run: {
      ...runSize(fontPx),
      ...(bold ? { bold: true } : {}),
    },
    paragraph: {
      spacing: {
        lineRule: LineRuleType.AT_LEAST,
        line: pxToTwips(linePx),
        before: pxToTwips(beforePx),
        after: pxToTwips(afterPx),
      },
    },
  };
}

function headingStyle(metrics: BlockMetrics) {
  const style = blockStyle(metrics);
  return { ...style, run: { ...style.run, ...EXPORTER_HEADING_DEFAULTS_OVERRIDE } };
}

// Plain paragraphs are emitted without a `w:pStyle`, so they resolve against
// `default.document` and never see the `Normal` style.
export const editorStyleOverrides = {
  default: { document: blockStyle(PARAGRAPH) },
  paragraphStyles: [
    { id: 'Normal', ...blockStyle(PARAGRAPH) },
    { id: 'Heading1', ...headingStyle(HEADING_1) },
    { id: 'Heading2', ...headingStyle(HEADING_2) },
    { id: 'Heading3', ...headingStyle(HEADING_3) },
    { id: 'Heading4', ...headingStyle(HEADING_4) },
    { id: 'Heading5', ...headingStyle(HEADING_5) },
    { id: 'Heading6', ...headingStyle(HEADING_6) },
    { id: 'ListParagraph', ...blockStyle(LIST_ITEM) },
    { id: 'Quote', ...blockStyle(QUOTE) },
  ],
};

const TABLE_GAP_PX = 12.5;

export const createTableSpacer = () =>
  new Paragraph({
    spacing: { before: 0, after: 0, line: pxToTwips(TABLE_GAP_PX), lineRule: LineRuleType.EXACT },
  });

export const editorTableCellMargins = {
  top: pxToTwips(TABLE_CELL_PADDING_PX),
  bottom: pxToTwips(TABLE_CELL_PADDING_PX),
  left: pxToTwips(TABLE_CELL_PADDING_PX),
  right: pxToTwips(TABLE_CELL_PADDING_PX),
};
